import { APP_REGISTRY, CyzorAppId } from '../navigation/appRegistry';
import {
  BusinessCapability,
  BusinessProfile,
  DashboardConfiguration,
  BusinessType,
} from './businessProfile';

const BASE_CAPABILITIES: BusinessCapability[] = ['CUSTOMER_MANAGEMENT', 'FINANCE', 'DOCUMENTS'];

const CAPABILITY_TO_APPS: Record<BusinessCapability, CyzorAppId[]> = {
  CUSTOMER_MANAGEMENT: ['crm'],
  PRODUCT_CATALOG: ['erp'],
  INVENTORY: ['erp'],
  SALES: ['erp'],
  PROJECT_MANAGEMENT: ['projects', 'people'],
  SERVICE_OPERATIONS: ['erp'],
  SCHEDULING: ['control'],
  SUPPLIERS: ['erp'],
  FINANCE: ['erp'],
  TEAM: ['people'],
  DOCUMENTS: ['docs'],
  ANALYTICS: ['bi'],
  SUBSCRIPTIONS: ['erp', 'bi'],
};

const DASHBOARD_BY_TYPE: Record<BusinessType, DashboardConfiguration> = {
  commerce: { profile: 'commerce', title: 'Visão comercial da operação', priorityMetrics: ['Vendas', 'Receita', 'Estoque baixo', 'Clientes'], quickActions: ['Novo produto', 'Novo cliente', 'Registrar venda'], sections: ['Produtos', 'Estoque', 'Fornecedores', 'Financeiro'] },
  services: { profile: 'services', title: 'Visão da operação de serviços', priorityMetrics: ['Projetos', 'Agenda', 'Clientes', 'Faturamento'], quickActions: ['Novo cliente', 'Novo projeto', 'Nova tarefa'], sections: ['Projetos', 'Equipe', 'Clientes', 'Documentos'] },
  industry: { profile: 'industry', title: 'Visão da operação industrial', priorityMetrics: ['Produção', 'Estoque', 'Custos', 'Fornecedores'], quickActions: ['Novo produto', 'Novo fornecedor', 'Registrar movimentação'], sections: ['Produtos', 'Estoque', 'Fornecedores', 'Financeiro'] },
  digital: { profile: 'digital', title: 'Visão do negócio digital', priorityMetrics: ['Receita recorrente', 'Clientes', 'Crescimento', 'Produtos'], quickActions: ['Novo cliente', 'Novo produto', 'Ver analytics'], sections: ['Produtos', 'Clientes', 'Financeiro', 'Analytics'] },
  hybrid: { profile: 'hybrid', title: 'Visão integrada da operação', priorityMetrics: ['Vendas', 'Projetos', 'Clientes', 'Receita'], quickActions: ['Novo produto', 'Novo projeto', 'Novo cliente'], sections: ['Produtos', 'Projetos', 'Clientes', 'Financeiro'] },
};

export function resolveCapabilities(profile: BusinessProfile): BusinessCapability[] {
  const capabilities = new Set<BusinessCapability>(BASE_CAPABILITIES);
  const operations = new Set(profile.operationalCapabilities);
  const channels = new Set(profile.salesChannels);

  if (['commerce', 'industry', 'digital', 'hybrid'].includes(profile.businessType) || profile.businessModel === 'saas' || profile.businessModel === 'digital_products') {
    capabilities.add('PRODUCT_CATALOG');
  }
  if (profile.businessType === 'commerce' || profile.businessType === 'industry' || operations.has('inventory')) capabilities.add('INVENTORY');
  if (profile.businessType === 'commerce' || channels.has('direct') || channels.has('physical_store') || channels.has('online_store')) capabilities.add('SALES');
  if (profile.businessType === 'services' || profile.businessType === 'hybrid' || operations.has('projects')) capabilities.add('SERVICE_OPERATIONS');
  if (operations.has('projects')) capabilities.add('PROJECT_MANAGEMENT');
  if (operations.has('scheduling')) capabilities.add('SCHEDULING');
  if (operations.has('suppliers') || profile.businessType === 'commerce' || profile.businessType === 'industry') capabilities.add('SUPPLIERS');
  if (operations.has('team') || profile.businessType === 'services' || profile.businessType === 'industry') capabilities.add('TEAM');
  if (profile.revenueModel === 'recurring' || profile.businessModel === 'saas') capabilities.add('SUBSCRIPTIONS');
  if (profile.businessType === 'digital' || operations.has('analytics')) capabilities.add('ANALYTICS');

  return Array.from(capabilities);
}

export function resolveModules(profile: BusinessProfile, plan?: { allowedApps?: string[] | null }): CyzorAppId[] {
  const resolved = new Set<CyzorAppId>(['control']);
  resolveCapabilities(profile).forEach((capability) => CAPABILITY_TO_APPS[capability].forEach((appId) => resolved.add(appId)));
  const allowedApps = plan?.allowedApps;
  return Array.from(resolved).filter((appId) => APP_REGISTRY[appId]?.enabled && (!allowedApps || allowedApps.includes(appId)));
}

export function resolveDashboard(profile: BusinessProfile): DashboardConfiguration {
  return DASHBOARD_BY_TYPE[profile.businessType] || DASHBOARD_BY_TYPE.services;
}

export function resolveBusinessExperience(profile: BusinessProfile, plan?: { allowedApps?: string[] | null }) {
  const capabilities = resolveCapabilities(profile);
  return { profile, capabilities, modules: resolveModules(profile, plan), dashboard: resolveDashboard(profile) };
}
